/**
 * components/shared/BrokerFees/FeeDetailModal.tsx
 * Comprehensive fee detail modal with asset-first organization
 * 
 * Clean HeroUI implementation displaying broker fees organized by asset category
 * with expandable sections. Uses HeroUI Modal, Card, and Accordion components.
 */

// 1. External dependencies
import React, { useState, useMemo } from 'react';
import { Search, Info, DollarSign, Percent, Minus } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Button, Card, CardBody, CardHeader, Accordion, AccordionItem, Alert } from '@enfomentis/ui';
import type { BrokerCatalogEntry } from '@/types/reference/BrokerCatalog';
import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import { useBrokerFeeDetails } from '@/hooks/useBrokerFeeDetails';
import type { AssetClassType, MarketSegment } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP, MARKET_SEGMENT_DISPLAY_MAP, SUB_ASSET_CLASS_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassDisplay, formatMarketSegmentDisplay } from '@/utils/assetClass';

// Fee formatting interfaces and types
interface FeeDisplayConfig {
  showCurrency?: boolean;
  showRoundTripIndicator?: boolean;
  compact?: boolean;
}

interface FormattedFee {
  amount: string;
  label: string;
  description: string;
  isPercentage: boolean;
  isSpreadOnly: boolean;
}

/**
 * Props for FeeDetailModal component
 */
export interface FeeDetailModalProps {
  /** Broker to show detailed fees for */
  broker: BrokerCatalogEntry;
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Asset class section for hierarchical display
 */
interface AssetClassSection {
  assetClass: string; // AssetClass enum value
  title: string; // Display name like "Futures", "Stocks"
  count: number; // Total fees in this asset class
  marketSegmentGroups: MarketSegmentGroup[];
}

/**
 * Market segment group within an asset class
 */
interface MarketSegmentGroup {
  marketSegment: string; // MarketSegment enum value
  title: string; // Display name like "Indices", "Metals"
  fees: BrokerFeeConfiguration[];
}

// Helper functions for fee formatting
const formatFee = (fee: BrokerFeeConfiguration, _config: FeeDisplayConfig = {}): FormattedFee => {
  const categoryName = fee.name || 'Unknown Fee';
    
  const commission = fee.commission;
  const isSpreadOnly = commission.feeType === 'spreadOnly';
  const isPercentage = commission.feeType === 'percentage';
  
  // Format basic amount for display
  let amount = 'Fee varies';
  if (isSpreadOnly) {
    amount = 'Spread only';
  } else if (commission.feeAmount) {
    const precision = commission.feePrecision || 2;
    if (isPercentage) {
      const percent = commission.feeAmount / 100;
      amount = `${percent.toFixed(2)}%`;
    } else {
      const dollars = commission.feeAmount / Math.pow(10, precision);
      amount = `$${dollars.toFixed(2)}`;
    }
  }
    
  return {
    amount,
    label: categoryName,
    description: fee.notes || 'Fee details',
    isPercentage,
    isSpreadOnly
  };
};

const sortFeesByPriority = (fees: BrokerFeeConfiguration[]): BrokerFeeConfiguration[] => {
  // Simple alphabetical sort for now
  return [...fees].sort((a, b) => {
    const aName = a.category ? `${a.category.assetClass}/${a.category.marketSegment}` : a.name || '';
    const bName = b.category ? `${b.category.assetClass}/${b.category.marketSegment}` : b.name || '';
    return aName.localeCompare(bName);
  });
};

/**
 * Comprehensive fee detail modal component with expandable sections
 */
// Helper functions for title formatting
// Local formatting functions removed - now using centralized utilities from AssetClass

export const FeeDetailModal: React.FC<FeeDetailModalProps> = ({
  broker,
  isOpen,
  onClose,
  className = ''
}) => {
  // Clean HeroUI Modal implementation - no theme hook needed
  const [searchTerm, setSearchTerm] = useState('');
  
  // ✅ HEAVY DATA LOADING - Only when modal is opened
  const { loading, error, data: feeDetails } = useBrokerFeeDetails(isOpen ? broker.id : null);

  // Fee display configuration
  const config = {
    showCurrency: true,
    showRoundTripIndicator: true,
    compact: false
  };

  // Organize fees by asset class hierarchy
  const assetClassSections = useMemo(() => {
    const sections: AssetClassSection[] = [];
    const allConfigs = feeDetails?.feeConfigurations || [];

    // Group configurations by asset class first
    const assetClassMap = new Map<AssetClassType, BrokerFeeConfiguration[]>();
    
    allConfigs.forEach(config => {
      if (config.category) {
        const assetClass = config.category.assetClass as AssetClassType;
        if (!assetClassMap.has(assetClass)) {
          assetClassMap.set(assetClass, []);
        }
        const assetClassConfigs = assetClassMap.get(assetClass);
        if (assetClassConfigs) {
          assetClassConfigs.push(config);
        }
      }
    });

    // Check if this is a single-asset broker (only one asset class)
    const isSingleAssetBroker = assetClassMap.size === 1;

    if (isSingleAssetBroker) {
      // For single-asset brokers, group directly by market segments (no asset class grouping)
      const allMarketSegmentMap = new Map<MarketSegment, BrokerFeeConfiguration[]>();
      const singleAssetClass = Array.from(assetClassMap.keys())[0];
      if (!singleAssetClass) return { sections: [], isSingleAssetBroker };
      const allConfigsForAsset = assetClassMap.get(singleAssetClass) || [];
      
      // Group all configurations by market segment
      allConfigsForAsset.forEach(config => {
        if (config.category && config.category.marketSegment) {
          const marketSegment = config.category.marketSegment as MarketSegment;
          if (!allMarketSegmentMap.has(marketSegment)) {
            allMarketSegmentMap.set(marketSegment, []);
          }
          const segmentConfigs = allMarketSegmentMap.get(marketSegment);
          if (segmentConfigs) {
            segmentConfigs.push(config);
          }
        }
      });

      // Create sections directly from market segments (treat each market segment as a "section")
      allMarketSegmentMap.forEach((segmentConfigs, marketSegment) => {
        sections.push({
          assetClass: singleAssetClass as string,
          title: formatMarketSegmentDisplay(marketSegment, MARKET_SEGMENT_DISPLAY_MAP), // Use market segment title instead of asset class
          count: segmentConfigs.length,
          marketSegmentGroups: [{
            marketSegment,
            title: formatMarketSegmentDisplay(marketSegment, MARKET_SEGMENT_DISPLAY_MAP),
            fees: sortFeesByPriority(segmentConfigs)
          }]
        });
      });
    } else {
      // For multi-asset brokers, use traditional asset class → market segment hierarchy
      assetClassMap.forEach((configs, assetClass) => {
        // Group by market segment within this asset class
        const marketSegmentMap = new Map<MarketSegment, BrokerFeeConfiguration[]>();
        
        configs.forEach(config => {
          if (config.category && config.category.marketSegment) {
            const marketSegment = config.category.marketSegment as MarketSegment;
            if (!marketSegmentMap.has(marketSegment)) {
              marketSegmentMap.set(marketSegment, []);
            }
            const segmentConfigs = marketSegmentMap.get(marketSegment);
            if (segmentConfigs) {
              segmentConfigs.push(config);
            }
          }
        });

        // Create market segment groups
        const marketSegmentGroups: MarketSegmentGroup[] = [];
        marketSegmentMap.forEach((segmentConfigs, marketSegment) => {
          marketSegmentGroups.push({
            marketSegment,
            title: formatMarketSegmentDisplay(marketSegment, MARKET_SEGMENT_DISPLAY_MAP),
            fees: sortFeesByPriority(segmentConfigs)
          });
        });

        // Sort market segment groups by title for consistent display
        marketSegmentGroups.sort((a, b) => a.title.localeCompare(b.title));

        sections.push({
          assetClass,
          title: formatAssetClassDisplay({ assetClass, marketSegment: 'indices' }, ASSET_CLASS_TYPE_DISPLAY_MAP, MARKET_SEGMENT_DISPLAY_MAP, SUB_ASSET_CLASS_DISPLAY_MAP),
          count: configs.length,
          marketSegmentGroups
        });
      });
    }

    // Sort sections by title for consistent display
    sections.sort((a, b) => a.title.localeCompare(b.title));

    return { sections, isSingleAssetBroker };
  }, [feeDetails?.feeConfigurations]);

  const { sections: assetClassSectionsData, isSingleAssetBroker } = assetClassSections;


  // Filter sections based on search term
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) {
      return assetClassSectionsData;
    }

    const term = searchTerm.toLowerCase();
    const filtered = assetClassSectionsData
      .map(section => {
        // Check if asset class title matches - if so, show entire section
        const sectionMatches = [
          section.title,
          section.assetClass
        ].join(' ').toLowerCase().includes(term);

        if (sectionMatches) {
          // Show entire section if asset class matches
          return section;
        }

        // Filter market segment groups within this asset class
        const filteredMarketSegmentGroups = section.marketSegmentGroups.map(group => ({
          ...group,
          fees: group.fees.filter(fee => {
            const formatted = formatFee(fee);
            
            // Special searches for fee types
            if (term === 'conversion' || term === 'currency conversion') {
              return !!fee.currencyConversion;
            }
            if (term === 'overnight' || term === 'overnight financing') {
              return !!fee.overnightFinancing;
            }
            if (term === 'commission') {
              return true; // All fees have commission
            }
            
            // Enhanced text search functionality
            const searchableText = [
              // Fee-specific fields
              formatted.label,
              formatted.description,
              fee.instrumentId || '',
              fee.notes || '',
              group.title, // Include market segment title
              
              // appliesTo array (instruments this config applies to)
              ...(fee.appliesTo || []),
              
              // Fee amounts (formatted for search)
              formatted.amount,
              
              // Commission details
              fee.commission.feeType,
              fee.commission.chargeOn,
              fee.commission.verificationStatus || '',
              
              // Fee type indicators
              fee.overnightFinancing ? 'overnight financing overnight' : '',
              fee.currencyConversion ? 'conversion currency conversion' : '',
              
              // Overnight financing details (if present)
              fee.overnightFinancing?.benchmark || '',
              fee.overnightFinancing?.frequency || '',
              fee.overnightFinancing?.assetClass || '',
              fee.overnightFinancing?.verificationStatus || '',
              
              // Currency conversion details (if present)
              fee.currencyConversion?.rateType || '',
              fee.currencyConversion?.verificationStatus || ''
            ].join(' ').toLowerCase();
            
            return searchableText.includes(term);
          })
        })).filter(group => group.fees.length > 0);
        
        // Calculate filtered count for this section
        const filteredCount = filteredMarketSegmentGroups.reduce(
          (total, group) => total + group.fees.length, 
          0
        );
        
        return {
          ...section,
          count: filteredCount, // Update count to reflect filtered results
          marketSegmentGroups: filteredMarketSegmentGroups
        };
      })
      .filter(section => section.marketSegmentGroups.length > 0);

    return filtered;
  }, [assetClassSectionsData, searchTerm]);

  // Don't render if not open
  if (!isOpen) return null;

  const totalFees = feeDetails?.feeConfigurations?.length || 0;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      className={className}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-lg md:text-xl font-bold">
                {broker.name} - Fee Structure
              </h2>
              <p className="text-xs md:text-sm text-default-500">
                {loading ? 'Loading fee details...' : 
                 error ? 'Fee details unavailable' :
                 `${totalFees} fee configuration${totalFees !== 1 ? 's' : ''} • ${broker.accountType === 'personal' ? 'Personal Account' : 'Prop Firm'}`}
              </p>
            </ModalHeader>

            <ModalBody className="space-y-6">
              {/* Search - Clean HeroUI Input */}
              <Input
                type="text"
                placeholder="Search by asset type (forex, stocks), fee type (conversion, overnight), instruments..."
                value={searchTerm}
                onValueChange={setSearchTerm}
                startContent={<Search className="text-default-400" size={16} />}
                description="Search by instrument name or type"
                variant="bordered"
              />
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-default-300 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-default-600">Loading fee details...</p>
                </div>
              ) : error ? (
                <Alert 
                  color="danger" 
                  variant="flat"
                  className="text-center"
                  title="Unable to load fee details"
                  description={error.message}
                  startContent={<Search className="w-5 h-5" />}
                />
              ) : filteredSections.length === 0 ? (
                <Alert 
                  color="default" 
                  variant="flat"
                  className="text-center"
                  title={searchTerm ? 'No fees match your search' : 'No fee information available'}
                  description={searchTerm ? 'Try adjusting your search terms' : 'Fee details are not available for this broker'}
                  startContent={<Search className="w-5 h-5" />}
                />
              ) : (
                <div className="space-y-6">
                  {isSingleAssetBroker ? (
                    /* Single-asset broker: show market segments directly without asset class grouping */
                    filteredSections.map((section) => (
                      <Card key={section.assetClass + section.title} className="border-t border-divider first:border-t-0">
                        <CardHeader className="pb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-foreground">
                              {section.title}
                            </h3>
                            <span className="text-sm text-default-500">
                              ({section.count})
                            </span>
                          </div>
                        </CardHeader>
                        <CardBody className="pt-0">
                          {/* Fees in this market segment - consistent grid layout */}
                          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 overflow-hidden">
                            {section.marketSegmentGroups[0]?.fees.map((fee, feeIndex) => (
                              <FeeDetailCard 
                                key={feeIndex} 
                                fee={fee} 
                                config={config}
                              />
                            )) || []}
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    /* Multi-asset broker: show hierarchical asset class → market segment structure */
                    <Accordion variant="splitted" selectionMode="multiple">
                      {filteredSections.map((section) => (
                        <AccordionItem
                          key={section.assetClass}
                          title={`${section.title} (${section.count})`}
                          className="border-t border-divider first:border-t-0"
                        >
                          <div className="space-y-4">
                            {section.marketSegmentGroups.map((group, groupIndex) => (
                              <Card key={groupIndex} className="border-l-2 border-primary/30">
                                <CardHeader className="pb-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-md font-medium text-foreground">
                                      {group.title}
                                    </h4>
                                    <span className="text-sm text-default-500">
                                      ({group.fees.length})
                                    </span>
                                  </div>
                                </CardHeader>
                                <CardBody className="pt-0">
                                  {/* Fees in this market segment - consistent grid layout */}
                                  <div className="grid gap-3 grid-cols-1 md:grid-cols-2 overflow-hidden">
                                    {group.fees.map((fee, feeIndex) => (
                                      <FeeDetailCard 
                                        key={feeIndex} 
                                        fee={fee} 
                                        config={config}
                                      />
                                    ))}
                                  </div>
                                </CardBody>
                              </Card>
                            ))}
                          </div>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </div>
              )}
            </ModalBody>
            
            <ModalFooter className="justify-center">
              <div className="text-center space-y-3">
                {/* Actionable message with info tooltip */}
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-default-600">
                    Fees not right?
                  </span>
                  <div className="group relative">
                    <Info className="w-4 h-4 text-default-500 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-content1 border border-divider shadow-medium text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 z-10">
                      <div className="space-y-1">
                        <p>Displayed fees will serve as reference.</p>
                        <p>You can modify them in settings and/or when you log your first trades.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button
                  color="primary"
                  variant="solid"
                  onPress={onClose}
                >
                  Close
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

/**
 * Individual fee detail card component (complete fee profile)
 */
interface FeeDetailCardProps {
  fee: BrokerFeeConfiguration;
  config: FeeDisplayConfig;
}

const FeeDetailCard: React.FC<FeeDetailCardProps> = ({ fee, config }) => {
  
  // Format the commission info
  const commissionInfo = formatFee(fee, config);
  
  // Get commission detail line
  const getCommissionDetail = () => {
    const commission = fee.commission;
    
    if (commission.feeType === 'spreadOnly') return 'Spread only';
    
    if (commission.feeType === 'perShare') {
      const amount = commission.feeAmount ? 
        `$${(commission.feeAmount / Math.pow(10, commission.feePrecision || 2)).toFixed(commission.feePrecision || 2)}` : '';
      const min = commission.minPerOrder ? 
        `$${(commission.minPerOrder / Math.pow(10, commission.minPerOrderPrecision || 2)).toFixed(commission.minPerOrderPrecision || 2)}` : null;
      return `${amount} per share${min ? `, ${min} minimum` : ''}`;
    }
    
    if (commission.feeType === 'perSharePlusBase') {
      const amount = commission.feeAmount ? 
        `$${(commission.feeAmount / Math.pow(10, commission.feePrecision || 2)).toFixed(commission.feePrecision || 2)}` : '';
      const base = commission.baseFee ? 
        `$${(commission.baseFee / Math.pow(10, commission.baseFeePrecision || 2)).toFixed(commission.baseFeePrecision || 2)}` : '';
      return `${amount} per share + ${base} base fee`;
    }
    
    if (commission.feeType === 'percentage') {
      const amount = commission.feeAmount ? 
        `${(commission.feeAmount / 100).toFixed(2)}%` : '';
      const min = commission.minPerOrder ? 
        `$${(commission.minPerOrder / Math.pow(10, commission.minPerOrderPrecision || 2)).toFixed(commission.minPerOrderPrecision || 2)}` : null;
      return `${amount} of trade value${min ? `, ${min} minimum` : ''}`;
    }
    
    if (commission.feeType === 'fixedPerUnit') {
      const amount = commission.feeAmount ? 
        `$${(commission.feeAmount / Math.pow(10, commission.feePrecision || 2)).toFixed(commission.feePrecision || 2)}` : '';
      const timingText = commission.chargeOn === 'roundTrip' ? ' round trip' : 
                        commission.chargeOn === 'bothLegs' ? ' per side' : 
                        commission.chargeOn === 'perSide' ? ' per side' : '';
      const min = commission.minPerOrder ? 
        `$${(commission.minPerOrder / Math.pow(10, commission.minPerOrderPrecision || 2)).toFixed(commission.minPerOrderPrecision || 2)}` : null;
      return `${amount}${timingText}${min ? `, ${min} minimum` : ''}`;
    }
    
    if (commission.feeType === 'fixedPerTransaction') {
      const amount = commission.feeAmount ? 
        `$${(commission.feeAmount / Math.pow(10, commission.feePrecision || 2)).toFixed(commission.feePrecision || 2)}` : '';
      return `${amount} per transaction`;
    }
    
    if (commission.feeType === 'tiered') {
      // For tiered pricing, show the first tier or indicate tiered structure
      return 'Tiered pricing - see details';
    }
    
    if (commission.feeType === 'makerTaker') {
      const makerPercent = commission.makerFee ? (commission.makerFee / 100).toFixed(2) : '0.00';
      const takerPercent = commission.takerFee ? (commission.takerFee / 100).toFixed(2) : '0.00';
      return `${makerPercent}% maker / ${takerPercent}% taker`;
    }
    
    // Fallback to formatted display with full description
    return commissionInfo.description;
  };
  
  // Get overnight financing info if available
  const getOvernightInfo = () => {
    const overnight = fee.overnightFinancing;
    if (!overnight) return null;
    const adminSpreadPercent = overnight.adminSpread / Math.pow(10, overnight.adminSpreadPrecision);
    return overnight.benchmark ? `${overnight.benchmark} ± ${adminSpreadPercent}%` : 'Rate varies';
  };
  
  // Get currency conversion info if available
  const getConversionInfo = () => {
    const conversion = fee.currencyConversion;
    if (!conversion) return null;
    return conversion.markup ? `${conversion.markup}%` : 'Rate varies';
  };

  const commissionDetail = getCommissionDetail();
  const overnightInfo = getOvernightInfo();
  const conversionInfo = getConversionInfo();

  const getIcon = () => {
    if (commissionInfo.isSpreadOnly) return <Minus className="w-4 h-4 text-success" />;
    if (commissionInfo.isPercentage) return <Percent className="w-4 h-4 text-primary" />;
    return <DollarSign className="w-4 h-4 text-primary" />;
  };

  return (
    <Card className="border border-divider hover:border-primary/50 transition-colors">
      <CardHeader className="pb-2">
        {/* Header: Category name with right-aligned icon */}
        <div className="flex items-center justify-between w-full">
          <h4 className="font-medium text-foreground">
            {commissionInfo.label}
          </h4>
          <div className="flex-shrink-0">{getIcon()}</div>
        </div>
      </CardHeader>
      
      <CardBody className="pt-0">
        {/* Details: All fee information in consistent format */}
        <div className="space-y-1">
          {/* Commission detail line */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-default-500">Commission:</span>
            <span className="text-default-700">{commissionDetail}</span>
          </div>
          
          {/* Overnight detail line */}
          {overnightInfo && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-default-500">Overnight:</span>
              <span className="text-default-700">{overnightInfo}</span>
            </div>
          )}
          
          {/* Conversion detail line */}
          {conversionInfo && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-default-500">Conversion:</span>
              <span className="text-default-700">{conversionInfo}</span>
            </div>
          )}
        </div>
        
        {fee.notes && (
          <p className="text-xs text-default-500 mt-2 italic break-words">
            {fee.notes}
          </p>
        )}
      </CardBody>
    </Card>
  );
};

